<script setup>
    import {computed, nextTick, onMounted, reactive, ref, watch} from 'vue'
    import BlockEditor from "./blockEditor.vue";
    import { useNotificationStore } from '@/stores/NotificationStore.js'
    import BlockSelector from "./blockSelector.vue";
    import { v4 as uuid } from 'uuid'
    import StyleEditor from "./styleEditor.vue";
    import PageBuilderParts from "./PageBuilderParts.vue";
    import RevisionsManager from "./RevisionsManager.vue";
    import SlideoutWithSlots from "../elements/slideoutWithSlots.vue";
    import IconPlus from "../icons/iconPlus.vue";
    import IconTrashcan from "../icons/iconTrashcan.vue";
    import IconSettings from "../icons/iconSettings.vue";
    import FrostedBar from "../layout/pageHeadings/frostedBar.vue";
    import IconChevron from "../icons/iconChevron.vue";
    import ButtonMoreOptions from "../buttons/buttonMoreOptions.vue";
    import ButtonMoreOptionsOption from "../buttons/buttonMoreOptionsOption.vue";
    import CssEditor from './CssEditor.vue';
    import InlinePreview from "./_inlinePreview.vue";
    import VisibilityEditor from "./visibilityEditor.vue";

    const notificationStore = useNotificationStore()

    const props = defineProps({
        containerType: {
            type: String,
            required: false,
            default: null,
        },
        containerRef: {
            type: String,
            required: true
        },
        defaultRowStyles: {
            type: Object,
            required: true,
        },
        defaultColStyles: {
            type: Object,
            required: true,
        },
        loadContentUrl: {
            type: String,
            required: true
        },
        updateContentUrl: {
            type: String,
            required: true
        },
        blockTypes: {
            type: Array,
            required: false,
            default: () => null
        },
        blockGroups: {
            type: Array,
            required: false,
            default: () => null
        },
        pageTitle: {
            type: String,
            required: false,
            default: _mft('pagebuilder:defaultTitle')
        },
        defaultFont: {
            type: String,
            required: false,
            default: 'Open Sans'
        },
        inlinePreviewUrl: {
            type: String,
            required: false,
            default: null,
        },
        whitelabelAppEnabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        metaDescription: {
            type: String,
            required: false,
            default: 'n/a',
        },
        hasPassword: {
            type: Boolean,
            required: false,
            default: false,
        },
    })

    const previewMode = ref(false)

    const blockEditorContent = ref(null)
    const pickedUpBlockSource = ref(null)
    const pickedUpBlockUuid = ref(null)
    const pickedUpColumnSource = ref(null)
    const pickedUpColumn = ref(null)
    const editingBlock = ref(null)
    const editingStyle = ref(null)
    const editingType = ref(null)
    const editingUuid = ref(null)
    const addingBlockAt = ref({row: null, col: null})
    const saving = ref(false)
    const savingState = ref('')
    const pageTitle = ref(props.pageTitle)
    const stateIsDirty = ref(false)
    const showPageSettings = ref(false)
    const showRevisions = ref(false)
    const devmodeEnabled = ref(false)
    const replaceColors = ref(false)
    const currentColors = ref({})
    const stagedForChange = ref(null)
    const newColor = ref(null)
    const showInlinePreview = ref(false)
    const editingVisibility = ref(null)
    const globalDesignSettings = ref(null)
    const expandedColumnTools = ref([])
    const addingFromSavedPageBuilderPartType = reactive({
        type: '',
        row: null,
        column: null,
    })

    const design = reactive({
        layout: {
            customcss: '',
            rows: []
        }
    })

    const loadAppPageSettings = async () => {
        const res = await axios.get(route('api.manage-whitelabel-app.settings'))
        globalDesignSettings.value = {
            appearance: res.data.design_configuration.appearance || {}
        }
    }

    const loadPage = async () => {
        const res = await axios.get(props.loadContentUrl)

        blockEditorContent.value = res.data.blockEditorContent
        design.layout = res.data.design.layout
        design.pageSettings = res.data.design.pageSettings
        design.pageSettings = setDefaultPageSettings(design.pageSettings)
        pageTitle.value = res.data.page.title
        await nextTick()
        stateIsDirty.value = false

        findHexCodesWithKeys(design.layout)
        if (design.pageSettings.font) {
            setFont('headings', design.pageSettings.font.headings.family)
            setFont('body', design.pageSettings.font.body.family)
        }
    }

    const setDefaultPageSettings = (pageSettings) => {
        if (! pageSettings) {
            pageSettings = {
                transitions: {
                    enableTransitions: true,
                }
            }
        }
        return pageSettings;
    }

    const updatePage = async ()  => {
        if (saving.value === true) {return;}
        savingState.value = 'waiting'

        saving.value = true
        try {
            const res = await axios.post(props.updateContentUrl, {
                blockEditorContent: blockEditorContent.value,
                design: design
            })
            if (res.data.success) {
                savingState.value = 'success'
            } else {
                savingState.value = 'error'
            }

            stateIsDirty.value = false
        } catch (e) {
            savingState.value = 'error'
        }
        saving.value = false

    }

    const getPersistentClass = (length) => {
        let result = 'mfpbr-persistent-';
        const characters = 'abcdefghijklmnopqrstuvwxyz123456789';
        let counter = 0;
        while (counter < length) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
            counter += 1;
        }
        return result;
    }

    const addRow = (row) => {
        if (row === null) {
            design.layout.rows.push({
                uuid: uuid(),
                persistentClass: getPersistentClass(8),
                styles: props.defaultRowStyles,
                columns: []
            })

            addCol(design.layout.rows.length - 1)
        } else {
            design.layout.rows.push(row)
        }
    }

    const addRowAtIndex = (row, index) => {
        let rowToInsert = row

        if (rowToInsert === null) {
            rowToInsert = {
                uuid: uuid(),
                persistentClass: getPersistentClass(8),
                styles: props.defaultRowStyles,
                columns: []
            }
        }

        design.layout.rows.splice(index, 0, rowToInsert)

        if (row === null) {
            addCol(index)
        }

        nextTick(() => {
            const rowElement = document.getElementById('pb-row-' + rowToInsert.uuid)
            if (!rowElement) {
                return
            }

            rowElement.classList.add('scale-95')
            setTimeout(function() {
                rowElement.classList.remove('scale-95')
            }, 150)
        })
    }

    const removeRow = (ri) => {
        if (design.layout.rows[ri].columns.length > 0) {
            Object.values(design.layout.rows[ri].columns).forEach(column => {
                Object.values(column.blocks).forEach(blockUuid => {
                    deleteBlock(blockUuid)
                });
            })
        }
        design.layout.rows.splice(ri, 1)
    }

    const addCol = (ri, column = null) => {
        design.layout.rows[ri].styles.desktop.layout = null
        if (column === null) {
            design.layout.rows[ri].columns.push({
                uuid: uuid(),
                styles: props.defaultColStyles,
                blocks: []
            })
        } else {
            design.layout.rows[ri].columns.push(column)
        }
    }

    const removeCol = (ri, ci) => {
        if (design.layout.rows[ri].columns[ci].blocks.length > 0) {
            Object.values(design.layout.rows[ri].columns[ci].blocks).forEach(blockUuid => {
                deleteBlock(blockUuid)
            });
        }
        design.layout.rows[ri].styles.desktop.layout = null
        design.layout.rows[ri].columns.splice(ci, 1)
    }

    const addBlock = (ri, ci) => {
        if (pickedUpBlockUuid.value !== null) {
            return
        }
        addingBlockAt.value = {row: ri, col: ci}
    }

    const initAddingColumnFromPageBuilderPart = (ri) => {
        addingFromSavedPageBuilderPartType.type = 'column';
        addingFromSavedPageBuilderPartType.row = ri;
    }

    const resetAddingFromSavedPageBuilderPartType = () => {
        addingFromSavedPageBuilderPartType.type = ''
        addingFromSavedPageBuilderPartType.row = null
        addingFromSavedPageBuilderPartType.column = null
    }

    const addFromPageBuilderPart = (part) => {

        if (addingFromSavedPageBuilderPartType.type === 'row') {
            for (const [key, value] of Object.entries(part.content)) {
                blockEditorContent.value.push(value)
            }
            addRow(part.design)
        }

        if (addingFromSavedPageBuilderPartType.type === 'column') {
            for (const [key, value] of Object.entries(part.content)) {
                blockEditorContent.value.push(value)
            }
            addCol(addingFromSavedPageBuilderPartType.row, part.design)
        }

        resetAddingFromSavedPageBuilderPartType()
    }

    const insertBlock = (block) => {
        if (!block.uuid) {
            block.uuid = uuid();
        }
        blockEditorContent.value.push(block)
        design.layout.rows[addingBlockAt.value.row].columns[addingBlockAt.value.col].blocks.push(block.uuid)
        addingBlockAt.value = {row: null, col: null}
        editingBlock.value = block
    }

    const pickUpBlock = (pickupData) => {
        pickedUpBlockUuid.value = pickupData.blockUuid
        pickedUpBlockSource.value = {row: pickupData.rowIndex, col: pickupData.colIndex}
    }

    const cloneBlock = (cloneData) => {
        const blockClone = JSON.parse(JSON.stringify(cloneData.block))
        blockClone.uuid = uuid()
        blockEditorContent.value.push(blockClone)
        design.layout.rows[cloneData.rowIndex].columns[cloneData.colIndex].blocks.splice(design.layout.rows[cloneData.rowIndex].columns[cloneData.colIndex].blocks.indexOf(cloneData.block.uuid) + 1, 0, blockClone.uuid)
    }

    const putBackPickedUpBlock = () => {
        pickedUpBlockUuid.value = null
        pickedUpBlockSource.value = null
    }

    const putBlockDown = (ri, ci, insertAfterBlockUuid) => {
        if (pickedUpBlockSource.value.row !== null && pickedUpBlockSource.value.col !== null) {
            design.layout.rows[pickedUpBlockSource.value.row].columns[pickedUpBlockSource.value.col].blocks = design.layout.rows[pickedUpBlockSource.value.row].columns[pickedUpBlockSource.value.col].blocks.filter(b => b !== pickedUpBlockUuid.value)
        }
        design.layout.rows[ri].columns[ci].blocks.splice(insertAfterBlockUuid ? design.layout.rows[ri].columns[ci].blocks.indexOf(insertAfterBlockUuid) + 1 : 0, 0, pickedUpBlockUuid.value)
        pickedUpBlockUuid.value = null
    }

    const deleteBlock = (blockUuid) => {
        blockEditorContent.value = blockEditorContent.value.filter(b => b.uuid !== blockUuid)
        design.layout.rows.forEach(row => {
            row.columns.forEach(col => {
                col.blocks = col.blocks.filter(b => b !== blockUuid)
            })
        })
    }
    const editBlock = (blockUuid) => {
        editingBlock.value = blockEditorContent.value.find(b => b.uuid === blockUuid)
    }

    const endAndSaveEditing = () => {
        editingBlock.value = null
        updatePage()
    }

    const endEditing = () => {
        editingBlock.value = null
    }

    const editStyle = (styles, type, uuid) => {
        editingUuid.value = uuid
        editingType.value = type
        editingStyle.value = styles
    }

    const updateStyle = (styles) => {
        if (editingType.value === 'row') {
            design.layout.rows.find(r => r.uuid === editingUuid.value).styles = styles
        }
        if (editingType.value === 'column') {
            const allCols = design.layout.rows.reduce((acc, row) => {
                return acc.concat(row.columns)
            }, [])

            // set style of column with uuid
            allCols.find(c => c.uuid === editingUuid.value).styles = styles
        }
    }

    const stopEditing = () => {
        editingUuid.value = null
        editingType.value = null
        editingStyle.value = null
    }

    const startEditingVisibility = (row) => {
        editingVisibility.value = row
    }

    const updateVisibilityOptions = (options) => {
        editingVisibility.value.visibilityOptions = options
        updatePage()
    }

    const stopEditingVisibility = () => {
        editingVisibility.value = null
    }

    const pickUpColumn = (rowIndex, columnIndex) => {
        const column = design.layout.rows[rowIndex].columns[columnIndex]
        pickedUpColumn.value = JSON.parse(JSON.stringify(column))
        pickedUpColumnSource.value = {row: rowIndex, col: columnIndex}

        // Remove the column from its current position
        design.layout.rows[rowIndex].columns.splice(columnIndex, 1)

        // Reset layout if columns configuration changed
        if (design.layout.rows[rowIndex].columns.length > 0) {
            design.layout.rows[rowIndex].styles.desktop.layout = null
        }
    }

    const putBackPickedUpColumn = () => {
        if (pickedUpColumnSource.value && pickedUpColumn.value) {
            // Restore the column to its original position
            design.layout.rows[pickedUpColumnSource.value.row].columns.splice(
                pickedUpColumnSource.value.col,
                0,
                pickedUpColumn.value
            )
        }
        pickedUpColumn.value = null
        pickedUpColumnSource.value = null
    }

    const putColumnDown = (targetRowIndex, targetColumnIndex) => {
        if (!pickedUpColumn.value) return

        // Check if the target row can accept more columns (max 5)
        if (design.layout.rows[targetRowIndex].columns.length >= 5) {
            notificationStore.addNotification({
                type: 'error',
                message: _mft('pagebuilder:column.maxColumnsReached'),
                dismissAfter: 3000,
            })
            return
        }

        // Insert the column at the target position
        if (targetColumnIndex === null) {
            // Add at the end of the row
            design.layout.rows[targetRowIndex].columns.push(pickedUpColumn.value)
        } else {
            // Insert at specific position
            design.layout.rows[targetRowIndex].columns.splice(targetColumnIndex, 0, pickedUpColumn.value)
        }

        // Clear the picked up column
        pickedUpColumn.value = null
        pickedUpColumnSource.value = null

        // Reset any custom layouts as column count may have changed
        design.layout.rows[targetRowIndex].styles.desktop.layout = null
    }

    const shiftRowUp = (ri) => {
        if (ri === 0) {
            return
        }
        const row = design.layout.rows.splice(ri, 1)[0]
        design.layout.rows.splice(ri - 1, 0, row)

        setTimeout(function(){
            window.scrollTo({
                top: document.getElementById('pb-row-' + row.uuid).offsetTop - 20,
                behavior: 'smooth',
            });
            setTimeout(function() {
                document.getElementById('pb-row-' + row.uuid).classList.add('scale-95');
            }, 100);
            setTimeout(function() {
                document.getElementById('pb-row-' + row.uuid).classList.remove('scale-95');
            }, 500);
        }, 50)

    }

    const shiftRowDown = (ri) => {
        if (ri === design.layout.rows.length - 1) {
            return
        }
        const row = design.layout.rows.splice(ri, 1)[0]
        design.layout.rows.splice(ri + 1, 0, row)

        setTimeout(function(){
            window.scrollTo({
                top: document.getElementById('pb-row-' + row.uuid).offsetTop - 20,
                behavior: 'smooth',
            });
            setTimeout(function() {
                document.getElementById('pb-row-' + row.uuid).classList.add('scale-95');
            }, 100);
            setTimeout(function() {
                document.getElementById('pb-row-' + row.uuid).classList.remove('scale-95');
            }, 500);
        }, 50)
    }

    const savePart = reactive({
        title: '',
        type: '',
        content: [],
        design: {}
    })

    const resetSavePart = () => {
        savePart.title = ''
        savePart.type = ''
        savePart.content = []
        savePart.design = {}
    }

    const saveRow = async (row) => {
        savePart.type = 'row'
        savePart.design = row

        row.columns.forEach(col => {
            col.blocks.forEach(blockUuid => {
                savePart.content.push(blockEditorContent.value.find(b => b.uuid === blockUuid))
            })
        })
    }

    const saveCol = (column) => {
        savePart.type = 'column'
        savePart.design = column

        column.blocks.forEach(blockUuid => {
            savePart.content.push(blockEditorContent.value.find(b => b.uuid === blockUuid))
        })
    }

    const saveBlock = (block) => {
        savePart.type = 'block'
        savePart.content = block
    }

    const savePageBuilderPart = async () => {
        const nonNullContentItems = savePart.content.filter(item => item !== null)

        const res = await axios.post(route('pages.pagebuilder.savePart'), {
            title: savePart.title,
            type: savePart.type,
            design: savePart.design,
            content: nonNullContentItems
        });

        resetSavePart()

        notificationStore.addNotification({
            type: 'success',
            message: _mft('shared:save.success'),
            dismissAfter: 2500,
        })
    }

    const selectForChange = (key, color) => {
        stagedForChange.value = { key, color };

    };

    const updateColors = () => {
        if (!stagedForChange.value || !newColor.value) {
            return;
        }
        replaceColorsInObject(design.layout, stagedForChange.value.color, newColor.value);
        stagedForChange.value = null;
        newColor.value = null;
        findHexCodesWithKeys(design.layout);
    };

    const replaceColorsInObject = (obj, colorToReplace, newColorValue) => {
        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                if (typeof obj[key] === 'object' && obj[key] !== null) {
                    replaceColorsInObject(obj[key], colorToReplace, newColorValue);
                } else if (typeof obj[key] === 'string') {
                    if (obj[key] === colorToReplace) {
                        obj[key] = newColorValue;
                    }
                }
            }
        }
    }

    const findHexCodesWithKeys = (obj) => {
        let hexCodes = {};

        function searchForHexCodes(obj, path = '') {
            for (let key in obj) {
                if (typeof obj[key] === 'object' && obj[key] !== null) {
                    searchForHexCodes(obj[key], path + key + '.');
                } else if (typeof obj[key] === 'string') {
                    let hexMatch = obj[key].match(/#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b|rgba?\(([^)]+)\)/);
                    if (hexMatch) {
                        const keyEnd = key.split('.').pop();

                        if (!hexCodes[keyEnd]) {
                            hexCodes[keyEnd] = [];
                        }

                        if (!hexCodes[keyEnd].includes(hexMatch[0])) {
                            hexCodes[keyEnd].push(hexMatch[0]);
                        }
                    }
                }
            }
        }

        searchForHexCodes(obj);
        currentColors.value = hexCodes;
    };

    const restoreRevision = (revision) => {
        blockEditorContent.value = revision.content
        design.layout = revision.design.layout
        design.pageSettings = revision.design.pageSettings
        design.pageSettings = setDefaultPageSettings(design.pageSettings)
        findHexCodesWithKeys(design.layout)
    }

    const setFont = (target, font) => {
        if (font) {
            if (! design.pageSettings.font) {
                design.pageSettings.font = {
                    headings: {
                        family: props.defaultFont
                    },
                    body: {
                        family: props.defaultFont
                    }
                }
            }
            design.pageSettings.font[target].family = font
            setDocumentFonts(design.pageSettings.font.headings.family, design.pageSettings.font.body.family)
        }
    }

    const setDocumentFonts = (headings, body) => {
        WebFont.load({
            google: {
                families: [headings + ':400,600,700', body + ':400,600,700']
            }
        });

        document.getElementById('pagebuilder-font').innerHTML = `
            html.wf-active .block-editor-blocks .styleable-block *:not(i[aria-hidden="true"]):not(h1, h2, h3, h4, h5, h6),
            .font-selector.body .font-selector-preview {
                font-family: ${body}, Open Sans;
            }

            html.wf-active .block-editor-blocks .styleable-block h1,
            html.wf-active .block-editor-blocks .styleable-block h2,
            html.wf-active .block-editor-blocks .styleable-block h3,
            html.wf-active .block-editor-blocks .styleable-block h4,
            html.wf-active .block-editor-blocks .styleable-block h5,
            html.wf-active .block-editor-blocks .styleable-block h6,
            html.wf-active .font-selector.headings .font-selector-preview {
                font-family: ${headings}, Open Sans !important;
            }
        `
    };

    const setAppDocumentFonts = () => {
        WebFont.load({
            google: {
                families: ['Open+Sans:400,600,700']
            }
        });

        document.getElementById('pagebuilder-font').innerHTML = `
            html .block-editor-blocks .styleable-block *:not(i[aria-hidden="true"]):not(h1, h2, h3, h4, h5, h6),
            .font-selector.body .font-selector-preview {
                font-family: Open Sans;
            }

            html .block-editor-blocks .styleable-block h1,
            html .block-editor-blocks .styleable-block h2,
            html .block-editor-blocks .styleable-block h3,
            html .block-editor-blocks .styleable-block h4,
            html .block-editor-blocks .styleable-block h5,
            html .block-editor-blocks .styleable-block h6,
            html .font-selector.headings .font-selector-preview {
                font-family: Open Sans !important;
            }
        `
    };

    const toggleEditingColumn = (uuid) => {
        if (expandedColumnTools.value.includes(uuid)) {
            expandedColumnTools.value = expandedColumnTools.value.filter(u => u !== uuid)
        } else {
            expandedColumnTools.value.push(uuid)
        }
    }

    const handlePreviewClick = () => {
        if (props.hasPassword) {
            // If page has password, open in new tab to avoid CORS issues with password form submission
            window.open(props.inlinePreviewUrl, '_blank')
        } else {
            // Otherwise, show inline preview as before
            showInlinePreview.value = true
        }
    }

    const defaultStyles = computed(() => {
        if (editingType.value === 'row') {
            return props.defaultRowStyles
        }
        if (editingType.value === 'column') {
            return props.defaultColStyles
        }
    })

    const showSavePartModal = computed(() => {
        return savePart.type !== ''
    })

    const pageBuilderStyles = computed(() => {
        if (globalDesignSettings.value === null) {
            return {}
        }

        return {
            backgroundColor: globalDesignSettings.value.appearance?.backgroundColor || '#ffffff',
            color: globalDesignSettings.value.appearance?.foregroundColor || '#000000',

            pinstripeColor: globalDesignSettings.value.appearance?.pinstripeColor,

            primaryBackgroundColor: globalDesignSettings.value.appearance?.primaryColor,
            primaryForegroundColor: globalDesignSettings.value.appearance?.primaryContrastColor,

            secondaryBackgroundColor: globalDesignSettings.value.appearance?.secondaryBackgroundColor,
            secondaryForegroundColor: globalDesignSettings.value.appearance?.secondaryForegroundColor,

            cardBackgroundColor: globalDesignSettings.value.appearance?.cardBackgroundColor,
            cardBorderColor: globalDesignSettings.value.appearance?.cardBorderColor,
            cardForegroundColor: globalDesignSettings.value.appearance?.cardForegroundColor,
        }

    })

    const hideEditorUi = computed(() => {
        return previewMode.value
    })

    watch(() => blockEditorContent, () => {
        stateIsDirty.value = true
    }, {deep: true})

    watch(() => design, () => {
        stateIsDirty.value = true;
        findHexCodesWithKeys(design.layout)
    }, {deep: true})

    watch(() => showPageSettings.value, () => {
        if (showPageSettings.value === false) {
            updatePage()
        }
    })

    onMounted(() => {
        window.myflow.pagebuilderActive = true
        window.myflow.pagebuilderMode = 'default'

        if (props.containerType === 'app_page') {
            window.myflow.pagebuilderMode = 'app_page'
            loadAppPageSettings()
            setAppDocumentFonts()
        }

        loadPage()

        window.onbeforeunload = function() {
            if (stateIsDirty.value === true) {
                return _mft('pagebuilder:warning.unsavedChanges')
            }
        }
    })

</script>

<template>
    <div class="page-builder-editor block-editor-blocks" :class="'page-builder-editor--'+props.containerType" :style="pageBuilderStyles">
        <div v-if="hideEditorUi && props.containerType ==='app_page'" class="fixed z-50 top-20 left-1/2 aspect-portraitVideo w-full max-w-sm -translate-x-1/2">
            <div class="relative w-full h-full">
                <div class="absolute right-full -top-20 h-screen w-screen bg-black" style="z-index: 9999;"></div>
                <div class="absolute left-full h-screen w-screen bg-black" style="z-index: 9999;"></div>
                <div class="absolute bottom-full h-screen w-screen bg-black" style="z-index: 9999;"></div>
                <div class="absolute top-full h-screen w-screen bg-black" style="z-index: 9999;"></div>
                <div class="absolute w-full h-full rounded-3xl -ml-2 -mt-2 border-8 border-black box-content"></div>
                <div class="absolute w-full h-full rounded-2xl border-2 border-gray-600 z-modal-base"></div>
            </div>
        </div>
        <div
            v-if="pickedUpBlockUuid"
            class="fixed text-xs transition-all top-2 right-2 z-modal"
        >
            <div
                :class="pickedUpBlockUuid ? 'scale-100 opacity-100 delay-75' : 'scale-0 opacity-0'"
                class="p-2 transition-all bg-white border shadow-md rounded-wl"
            >
                <button-danger
                    size="sm"
                    @click="putBackPickedUpBlock"
                >{{ _mft('pagebuilder:block.cancelMove') }}</button-danger>
            </div>
        </div>

        <div
            v-if="pickedUpColumn"
            class="fixed text-xs transition-all top-2 right-2 z-modal"
        >
            <div
                :class="pickedUpColumn ? 'scale-100 opacity-100 delay-75' : 'scale-0 opacity-0'"
                class="p-2 transition-all bg-white border shadow-md rounded-wl"
            >
                <button-danger
                    size="sm"
                    @click="putBackPickedUpColumn"
                >{{ _mft('pagebuilder:column.cancelMove') }}</button-danger>
            </div>
        </div>

        <BlockEditor
            v-if="editingBlock"
            @done="endAndSaveEditing"
            @close="endEditing"
            :block="editingBlock"
        >
            <template #sloteditortools="{ block }">
                <slot
                    name="sloteditor"
                    :block="block"
                ></slot>
            </template>
        </BlockEditor>


        <Teleport to="#teleport-target-sticky-before-main" v-if="blockEditorContent">
            <frosted-bar class="transition-transform" :class="pickedUpBlockUuid ? '-translate-y-24' : ''">
                <div class="items-center justify-between space-x-4 md:flex">
                    <h1 class="overflow-hidden md:whitespace-nowrap overflow-ellipsis">{{ pageTitle }}</h1>
                    <div class="flex items-center justify-end space-x-2">

                        <warnings-errors
                            :blockEditorContent="blockEditorContent"
                            :design="design"
                            :pageTitle="pageTitle"
                            :metaDescription="metaDescription"
                            :containerType="containerType"
                        />


                        <slot name="toolbar"></slot>

                        <button-more-options
                            alignment="right"
                            :cancelOnOutsideClick="false"
                        >
                            <button-more-options-option
                                class="text-xs whitespace-nowrap"
                                @click="showPageSettings = true"
                                :close-on-click="true"
                            >
                                {{ _mft('pagebuilder:pageSettings.showSettingsLabel') }}
                            </button-more-options-option>

                            <button-more-options-option
                                class="text-xs whitespace-nowrap"
                                @click="showRevisions = true"
                                :close-on-click="true"
                            >
                                {{ _mft('pagebuilder:showRevisions') }}
                            </button-more-options-option>

                            <button-more-options-option
                                class="text-xs whitespace-nowrap"
                                @click="replaceColors = true"
                                :close-on-click="true"
                            >
                                {{ _mft('pagebuilder:colorReplacer.button') }}
                            </button-more-options-option>

                            <button-more-options-option
                                v-if="devmodeEnabled === false"
                                class="text-xs whitespace-nowrap"
                                @click="devmodeEnabled = true"
                                :close-on-click="true"
                            >
                                {{ _mft('pagebuilder:cssEditor.enableDevmode') }}
                            </button-more-options-option>

                            <button-more-options-option
                                v-if="devmodeEnabled === true"
                                class="text-xs whitespace-nowrap"
                                @click="devmodeEnabled = false"
                                :close-on-click="true"
                            >
                                {{ _mft('pagebuilder:cssEditor.disableDevmode') }}
                            </button-more-options-option>

                            <slot name="more-options"></slot>
                        </button-more-options>

                        <button-primary
                            v-if="props.containerType === 'app_page'"
                            @click="previewMode = ! previewMode"
                        >
                            {{ previewMode ? _mft('shared:edit') : _mft('shared:preview') }}
                        </button-primary>

                        <button-primary
                            v-if="inlinePreviewUrl && props.containerType !== 'app_page'"
                            @click="handlePreviewClick"
                        >
                            {{ _mft('shared:preview') }}
                        </button-primary>

                        <button-success
                            :disabled="saving"
                            @click="updatePage"
                            :state="savingState"
                        >{{ _mft('shared:action.save') }}</button-success>
                    </div>
                </div>
            </frosted-bar>
        </Teleport>
        <div :class="[containerType === 'app_page' ? 'max-w-sm mx-auto relative' : '', hideEditorUi && containerType === 'app_page' ? 'px-0.5 pb-[50vh]' : '']">
            <div
                v-for="(row, ri) in design.layout.rows"
                :key="ri"
                :id="'pb-row-'+row.uuid"
                class="relative flex-row-reverse lg:flex transition-transform group/row"
                :class="hideEditorUi ? '' : 'border-2 border-blue-200 rounded-r-md rounded-l-wl'"
            >
                <span
                    v-if="devmodeEnabled && ! hideEditorUi"
                    class="absolute -top-3 rounded-md bg-white/70 p-1 right-0.5 text-xxs z-tooltip"
                >
                    <span class="select-all">.mfpbr.mfpbr-{{ row.uuid }}</span>
                    <span v-if="row.persistentClass">, <br> <span class="select-all">.{{ row.persistentClass }}</span></span>
                </span>
                <div
                    v-if="! hideEditorUi"
                    class="flex items-center justify-end p-1 mb-1 space-x-1 bg-blue-200 lg:space-x-0 lg:space-y-2 lg:mb-0 lg:flex-col lg:justify-start"
                >
                    <button @click="shiftRowUp(ri)">
                        <icon-chevron
                            class="w-4 h-4 rotate-180"
                            :class="ri === 0 ? 'fill-gray-500/30 cursor-not-allowed' : 'fill-gray-500'"
                        />
                    </button>
                    <button-with-confirmation
                        class="mb-0.5"
                        size="sm"
                        style-type="transparent"
                        @confirmed="removeRow(ri)"
                        modalClass="absolute -top-2 -bottom-2 -right-2 p-2 bg-white rounded shadow-md z-20"
                        :confirmButtonLabel="_mft('shared:action.delete')"
                        confirmButtonClass="myflow-basic-button--danger whitespace-nowrap"
                    >
                        <icon-trashcan class="w-4 h-4 text-danger" />
                    </button-with-confirmation>
                    <button-default
                        size="sm"
                        style-type="transparent"
                        @click="editStyle(row.styles, 'row', row.uuid)"
                    >
                        <icon-settings class="w-4 h-4" />
                    </button-default>

                    <span class="relative">
                        <button-more-options
                            size="sm"
                            alignment="right"
                            style-type="transparent"
                        >
                            <button-more-options-option
                                class="text-xs whitespace-nowrap"

                                @click="saveRow(row)"
                            >
                                Save row
                            </button-more-options-option>
                            <button-more-options-option
                                v-if="['customerarea', 'app_page'].includes(props.containerType)"
                                class="text-xs whitespace-nowrap"

                                @click="startEditingVisibility(row)"
                            >
                                Visibility settings
                            </button-more-options-option>
                        </button-more-options>
                        <span
                            v-if="row.visibilityOptions?.permittingTags?.length > 0 || row.visibilityOptions?.prohibitingTags?.length > 0"
                            class="absolute -top-2 right-0 pointer-events-none"
                        >
                            <i class="fa-solid fa-eye text-xs text-gray-500 top-1 right-0 absolute z-50"></i>
                        </span>
                    </span>
                    <button-more-options
                        size="sm"
                        alignment="right"
                        style-type="transparent"
                        v-if="(containerType !== 'app_page' || row.columns.length < 2) && row.columns.length < 5"
                    >
                        <template #icon>
                            <icon-plus class="w-4 h-4 text-success-dark" />
                        </template>
                        <button-more-options-option
                            @click="addCol(ri)"
                            class="whitespace-nowrap"
                        >
                            {{ _mft('pagebuilder:addColumn.empty') }}
                        </button-more-options-option>

                        <button-more-options-option
                            @click="initAddingColumnFromPageBuilderPart(ri)"
                            class="whitespace-nowrap"
                        >
                            {{ _mft('pagebuilder:addColumn.saved') }}
                        </button-more-options-option>
                    </button-more-options>

                    <button @click="shiftRowDown(ri)">
                        <icon-chevron
                            class="w-4 h-4"
                            :class="ri === design.layout.rows.length - 1 ? 'fill-gray-500/30 cursor-not-allowed' : 'fill-gray-500'"
                        />
                    </button>
                </div>
                <div class="order-1 order-2 order-3 order-4 order-5"></div>
                <page-builder-row
                    :styles="row.styles"
                    :container-ref="containerRef"
                    :container-type="containerType"
                    :row-uuid="row.uuid"
                    :persistent-class="row.persistentClass"
                    class="mfpb-row"
                >
                    <div
                        class="relative flex flex-col"
                    >
                        <!-- Place column buttons row above columns -->
                        <div v-if="pickedUpColumn && row.columns.length < 5" class="flex justify-around mb-2">
                            <button
                                v-for="(_, index) in row.columns.length + 1"
                                :key="'place-' + index"
                                class="py-1 px-3 text-xs text-black bg-gray-100 border-2 border-gray-300 rounded-lg hover:border-gray-400"
                                @click="putColumnDown(ri, index)"
                            >
                                {{ _mft('pagebuilder:column.placeHere') }}
                            </button>
                        </div>

                        <div
                            class="grid w-full gap-1 grow"
                            :class="['grid-cols-1', row.styles.desktop.layout ? 'md:grid-cols-' + row.styles.desktop.layout?.split('|')[0] : ' md:grid-cols-' + row.columns.length]"
                        >
                            <template v-for="(column, ci) in row.columns" :key="ci">
                                <div
                                    class="relative transition-all mfpbc group/column"
                                    :class="[hideEditorUi ? '' : 'border-2 border-slate-200 rounded-wl hover:border-gray-300', row.styles.desktop.layout ? 'col-span-' + row.styles.desktop.layout?.split('|')[1].split('+')[ci] : 'col-span-1', row.styles.mobile.rowColumnOrder === 'reverse' ? 'md:order-none order-'+(row.columns.length - ci) : '', 'mfpbc-' + column?.uuid, pickedUpColumn && pickedUpColumnSource?.row === ri && pickedUpColumnSource?.col === ci ? 'opacity-25' : '']"
                                >
                                    <span
                                        v-if="devmodeEnabled"
                                        class="absolute top-2 select-all rounded-md bg-white/70 p-1 -left-0.5 text-xxs z-tooltip"
                                    >
                                        .mfpbc.mfpbc-{{ column.uuid }}
                                    </span>
                                    <div
                                        v-if="! expandedColumnTools.includes(column.uuid)"
                                        class="absolute top-0 z-50 left-0 w-8 h-7 bg-slate-200 group-hover/column:bg-gray-300 flex items-center justify-center rounded-tl-wl rounded-br-lg -mt-px -ml-px"
                                    >
                                        <button-transparent
                                            size="line"
                                            @click="toggleEditingColumn(column.uuid)"
                                        >
                                            <i class="fa-light fa-cog m-2 text-gray-600"></i>
                                        </button-transparent>
                                    </div>
                                    <div
                                        v-if="!pickedUpBlockUuid && !pickedUpColumn && ! hideEditorUi && expandedColumnTools.includes(column.uuid)"
                                        class="absolute top-0 left-0 z-modal flex items-center justify-end px-1 mb-0 space-x-1 transition-all transform rounded-tl-wl rounded-br-lg bg-slate-200 group-hover/column:bg-gray-300 -ml-px -mt-px"
                                    >
                                        <div class="flex items-center space-x-1">
                                            <button-default
                                                size="sm"
                                                style-type="transparent"
                                                @click="toggleEditingColumn(column.uuid)"
                                            >
                                                <i class="fa-regular fa-close"></i>
                                            </button-default>
                                            <button-more-options
                                                size="sm"
                                                style-type="transparent"
                                                alignment="left"
                                                :cancel-on-outside-click="false"
                                            >
                                                <button-more-options-option
                                                    class="text-xs whitespace-nowrap"
                                                    @click="saveCol(column)"
                                                >
                                                    Save column
                                                </button-more-options-option>
                                            </button-more-options>
                                            <button-default
                                                size="sm"
                                                style-type="transparent"
                                                @click="pickUpColumn(ri, ci)"
                                            >
                                                <i class="fa-regular fa-arrows-up-down-left-right"></i>
                                            </button-default>
                                            <button-default
                                                size="sm"
                                                style-type="transparent"
                                                @click="editStyle(column.styles, 'column', column.uuid)"
                                            >
                                                <icon-settings class="w-4 h-4" />
                                            </button-default>

                                            <button-with-confirmation
                                                class="mb-0.5"
                                                size="sm"
                                                style-type="transparent"
                                                @confirmed="removeCol(ri, ci)"
                                                modalClass="absolute -top-2 -bottom-2 -right-2 p-2 bg-white rounded shadow-md z-20"
                                                :confirmButtonLabel="_mft('shared:action.delete')"
                                                confirmButtonClass="myflow-basic-button--danger whitespace-nowrap"
                                            >
                                                <icon-trashcan class="w-4 h-4 text-danger" />
                                            </button-with-confirmation>
                                        </div>
                                    </div>

                                    <div class="mfpb-col-container">
                                        <div class="relative">
                                            <button v-if="pickedUpBlockUuid" class="absolute top-0 z-20 py-1 text-xs text-black transform -translate-y-1/2 bg-gray-100 border-2 border-gray-300 right-4 left-4 rounded-wl hover:border-gray-400" @click="putBlockDown(ri, ci, null)">{{ _mft('pagebuilder:block.moveHere') }}</button>
                                        </div>
                                        <page-builder-column
                                            :styles="column.styles"
                                            class="overflow-hidden rounded-wl"
                                            :class="pickedUpBlockUuid ? '!pb-6' : ''"
                                            :container-type="containerType"
                                        >
                                            <div
                                                v-for="(blockUuid, bi) in column.blocks"
                                                :key="bi"
                                                :data-block-uuid="blockUuid"
                                                class="relative group/block"
                                            >
                                                <span
                                                    v-if="devmodeEnabled && ! hideEditorUi"
                                                    class="absolute top-8 select-all rounded-md bg-white/70 p-1 right-0.5 text-xxs z-tooltip"
                                                >
                                                    .mfpbb.mfpbb-{{ blockUuid }}
                                                </span>
                                                <page-builder-block-edit
                                                    :rowIndex="ri"
                                                    :colIndex="ci"
                                                    :blockIndex="bi"
                                                    :container-ref="containerRef"
                                                    :block="blockEditorContent.find(b => b.uuid === blockUuid)"
                                                    :isPickedUp="blockUuid === pickedUpBlockUuid"
                                                    :hide-editor-ui="hideEditorUi"
                                                    @chosenForEditing="editBlock"
                                                    @deleted="deleteBlock"
                                                    @pickedUp="pickUpBlock"
                                                    @clone="cloneBlock"
                                                    @save="saveBlock"
                                                />
                                                <div class="relative">
                                                    <button v-if="pickedUpBlockUuid" class="absolute top-0 z-20 py-1 text-xs text-black transform -translate-y-1/2 bg-gray-100 border-2 border-gray-300 right-4 left-4 rounded-wl hover:border-gray-400" @click="putBlockDown(ri, ci, blockUuid)">{{ _mft('pagebuilder:block.moveHere') }}</button>
                                                </div>
                                            </div>
                                        </page-builder-column>
                                        <div v-if="! hideEditorUi" class="flex justify-end px-5 py-1">
                                            <button-default
                                                :disabled="!!pickedUpBlockUuid"
                                                size="sm"
                                                @click="addBlock(ri, ci)"
                                            >
                                                <template #icon>
                                                    <icon-plus class="w-4 h-4 -ml-1" />
                                                </template>
                                                {{ _mft('pagebuilder:column.addContent') }}
                                            </button-default>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </page-builder-row>
                <div
                    class="absolute z-20 -top-1 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden group-hover/row:block"
                >
                    <div
                        v-if="! hideEditorUi"
                        class="flex justify-center py-2 space-x-4"
                    >
                        <button
                            @click="addRowAtIndex(null, ri)"
                            class="relative group w-6 h-6 -translate-x-1/2"
                        >
                            <span
                                class="absolute inset-0 z-20 w-6 h-6 border-2 border-gray-400/50 rounded-full hidden group-hover/row:flex justify-center items-center bg-white/70 group-hover:bg-white shadow-lg"
                            >
                                <icon-plus class="w-4 h-4 text-gray-500/70 group-hover:text-gray-600 transition-colors" />
                            </span>
                            <span class="absolute h-6 opacity-0 group-hover:opacity-100 -left-1 top-0 z-10 pl-4 pr-1.5 translate-x-3 group-hover:translate-x-4 transition-all bg-gray-50/90 py-1 rounded-l-lg group-hover:rounded-l-none rounded-r-full whitespace-nowrap border-2 border-l-0 border-gray-400/50 text-xxs flex items-center pointer-events-none text-gray-500">
                                {{ _mft('pagebuilder:addRow.empty.long') }}
                            </span>
                        </button>
                    </div>
                </div>

                <div
                    class="absolute z-20 top-full left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 hidden group-hover/row:block"
                >
                    <div
                        v-if="! hideEditorUi"
                        class="flex justify-center py-2 space-x-4"
                    >
                        <button
                            @click="addRowAtIndex(null, ri+1)"
                            class="relative group w-6 h-6 -translate-x-1/2"
                        >
                            <span
                                class="absolute inset-0 z-20 w-6 h-6 border-2 border-gray-400/50 rounded-full hidden group-hover/row:flex justify-center items-center bg-white/70 group-hover:bg-white shadow-lg"
                            >
                                <icon-plus class="w-4 h-4 text-gray-500/70 group-hover:text-gray-600 transition-colors" />
                            </span>
                            <span class="absolute h-6 opacity-0 group-hover:opacity-100 -left-1 top-0 z-10 pl-4 pr-1.5 translate-x-3 group-hover:translate-x-4 transition-all bg-gray-50/90 py-1 rounded-l-lg group-hover:rounded-l-none rounded-r-full whitespace-nowrap border-2 border-l-0 border-gray-400/50 text-xxs flex items-center pointer-events-none text-gray-500">
                                {{ _mft('pagebuilder:addRow.empty.long') }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="! hideEditorUi" class="flex justify-center py-2 mt-1 mb-24 space-x-4 border-2 border-gray-300 rounded-wl">
                <button-base
                    style-type="secondary"
                    @click="addRow(null)"
                    size="sm"
                    class="whitespace-nowrap"
                >
                    <template #icon>
                        <icon-plus class="w-4 h-4 -ml-1" />
                    </template>
                    {{ _mft('pagebuilder:addRow.empty.long') }}
                </button-base>

                <button-base
                    style-type="secondary"
                    @click="addingFromSavedPageBuilderPartType.type = 'row'"
                    size="sm"
                    class="whitespace-nowrap"
                >
                    <template #icon>
                        <icon-plus class="w-4 h-4 -ml-1" />
                    </template>
                    {{ _mft('pagebuilder:addRow.saved.long') }}
                </button-base>
            </div>
        </div>

        <Teleport
            v-if="inlinePreviewUrl"
            to="body"
        >
            <InlinePreview
                v-if="showInlinePreview"
                :url="inlinePreviewUrl"
                :container-type="containerType"
                @close="showInlinePreview = false"
            />
        </Teleport>


        <BlockSelector
            v-if="addingBlockAt.row !== null"
            @close="addingBlockAt = {row: null, col: null}"
            @blockSelected="insertBlock"
            :blockTypes="blockTypes"
            :blockGroups="blockGroups"
            :whitelabel-app-enabled="props.whitelabelAppEnabled"
        />

        <StyleEditor
            v-if="editingStyle !== null"
            @close="stopEditing"
            @save="updatePage"
            :styles="editingStyle"
            :default-styles="defaultStyles"
            :col-count="editingType !== 'row' ? null : design.layout.rows[design.layout.rows.findIndex(r => r.uuid === editingUuid)].columns.length"
            :whitelabel-app-enabled="props.whitelabelAppEnabled"
            :container-type="containerType"
            @update:style="updateStyle"
        />

        <VisibilityEditor
            v-if="editingVisibility !== null"
            @close="stopEditingVisibility"
            :visibility-options="editingVisibility.visibilityOptions"
            @update:visibility-options="updateVisibilityOptions"
        ></VisibilityEditor>

        <PageBuilderParts
            :type="addingFromSavedPageBuilderPartType.type"
            @add="addFromPageBuilderPart"
            @close="resetAddingFromSavedPageBuilderPartType"
        ></PageBuilderParts>

        <RevisionsManager
            v-if="showRevisions"
            :container-ref="containerRef"
            @close="showRevisions = false"
            @restore-revision="restoreRevision"
        ></RevisionsManager>

        <slideout-with-slots
            v-if="showPageSettings"
            @close="showPageSettings = false"
            :title="_mft('pagebuilder:pageSettings.title')"
        >
            <template #body>
                <div class="space-y-4">
                    <div
                        v-if="containerType !== 'app_page'"
                        class="mb-4"
                    >
                        <input-label :label="_mft('page:fontTarget.heading')"></input-label>
                        <block-editor-font-options
                            class="w-full font-selector headings"
                            :font="design.pageSettings.font?.headings?.family ? design.pageSettings.font?.headings?.family : defaultFont"
                            @update:modelValue="setFont('headings', $event)"
                        />
                    </div>
                    <div
                        v-if="containerType !== 'app_page'"
                        class="mb-8"
                    >
                        <input-label :label="_mft('page:fontTarget.body')"></input-label>
                        <block-editor-font-options
                            class="w-full font-selector body"
                            :font="design.pageSettings.font?.body?.family ? design.pageSettings.font?.body?.family : defaultFont"
                            @update:modelValue="setFont('body', $event)"
                        />
                    </div>

                    <toggle-switch
                        :label="_mft('pagebuilder:pageSettings.transitions.enableTransitions')"
                        v-model:content="design.pageSettings.transitions.enableTransitions"
                        class="pt-3"
                    ></toggle-switch>

                    <div class="pt-4">
                        <input-label :label="_mft('pagebuilder:cssEditor.editCss')" />
                        <div class="w-full mb-4">
                            <CssEditor v-model="design.layout.customcss" />
                        </div>
                        <p
                            class="text-xs text-gray-500"
                            v-html="_mft('pagebuilder:cssEditor.withGreatPower')"
                        ></p>
                    </div>

                    <div class="flex justify-end">
                        <button-primary
                            @click="showPageSettings = false"
                        >
                            {{ _mft('shared:action.save') }}
                        </button-primary>
                    </div>
                </div>
            </template>
        </slideout-with-slots>

        <slideout-with-slots
            v-if="replaceColors"
            @close="replaceColors = false"
        >
            <template #body>
                <h3 class="mt-5 mb-4">{{ _mft('pagebuilder:colorReplacer.mark') }}</h3>
                <div
                    v-for="(color, key) in currentColors"
                    :key="key"
                    class="mb-2"
                >
                    <input-label
                        v-if="! stagedForChange"
                        :label="_mft('pagebuilder:styleEditor.'+key)"
                    />

                    <div v-for="c in color" :key="c">
                        <input-label
                            v-if="stagedForChange && stagedForChange.key === key && stagedForChange.color"
                            :label="_mft('pagebuilder:styleEditor.'+key)"
                        />

                        <div
                            v-if="!stagedForChange  || stagedForChange && stagedForChange.key === key && stagedForChange.color"
                            class="flex items-center overflow-hidden border rounded-lg cursor-pointer"
                            v-on:click="selectForChange(key, c)"
                            :class="stagedForChange && stagedForChange.key === key && stagedForChange.color === c ? 'border-gray-500' : 'border-gray-100'"
                        >
                            <div
                                :style="{ backgroundColor: c }"
                                class="w-12 h-8 border-r"
                                :class="stagedForChange && stagedForChange.key === key && stagedForChange.color === c ? 'border-gray-500' : 'border-gray-100'"
                            ></div>
                            <div class="px-3 py-1 text-xs">{{ c }}</div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="stagedForChange"
                >
                    <h3 class="mt-5">{{ _mft('pagebuilder:colorReplacer.replace') }}</h3>
                    <color-picker
                        :color="newColor"
                        @colorChanged="color => newColor = color"
                        :show-palette="true"
                        format="prgb"
                        :disable-alpha="false"
                    ></color-picker>

                    <div class="flex justify-end space-x-2">
                        <button-danger
                            v-on:click="stagedForChange = null"
                            class="mt-4"
                        >
                            {{ _mft('shared:cancel') }}
                        </button-danger>
                        <button-success
                            v-on:click="updateColors"
                            class="mt-4"
                        >
                            {{ _mft('shared:action.update') }}
                        </button-success>
                    </div>
                </div>
            </template>
        </slideout-with-slots>

        <modal-basic
            :open="showSavePartModal"
            @closed="resetSavePart"
        >
            <template #header>
                {{ _mft('pagebuilder:savePart.'+ savePart.type +'.title') }}
            </template>
            <template #body>
                <input-text v-model:content="savePart.title" label="Title" />
                <div class="flex justify-end">
                    <button-primary
                        size="sm"
                        @click="savePageBuilderPart"
                    >{{ _mft('shared:action.save') }}</button-primary>
                </div>
            </template>
        </modal-basic>

        <Teleport to="#page-builder-custom-css">
            {{ design.layout.customcss }}
        </Teleport>

    </div>
</template>

<style scoped>
    .page-builder-editor--app_page {
        --pb-background-color: v-bind('pageBuilderStyles.backgroundColor');
        --pb-foreground-color: v-bind('pageBuilderStyles.color');

        --pb-pinstripe-color: v-bind('pageBuilderStyles.pinstripeColor');

        --pb-primary-background-color: v-bind('pageBuilderStyles.primaryBackgroundColor');
        --pb-primary-foreground-color: v-bind('pageBuilderStyles.primaryForegroundColor');

        --pb-secondary-background-color: v-bind('pageBuilderStyles.secondaryBackgroundColor');
        --pb-secondary-foreground-color: v-bind('pageBuilderStyles.secondaryForegroundColor');

        --pb-card-background-color: v-bind('pageBuilderStyles.cardBackgroundColor');
        --pb-card-border-color: v-bind('pageBuilderStyles.cardBorderColor');
        --pb-card-foreground-color: v-bind('pageBuilderStyles.cardForegroundColor');
    }
</style>
